import { useCallback, useEffect, useState } from 'react';

import { api, type SzallitolevelSor } from '../api.ts';
import { AlairasGomb } from './AlairasGomb.tsx';
import {
  NYOMTATO_ALAP,
  raklapElonezet,
  raklapotNyomtat,
  type NyomtatoBeallitas,
} from './cimke-nyomtato.ts';
import { useFelhasznalo, useMuvelet } from '../felhasznalo-context.ts';
import { useMobil } from '../mobil-context.ts';
import { ft, szam } from '../formazas.ts';

/**
 * Autón · Raktár · Kiadott. (3.0 / 5.4–5.5)
 *
 * Három nézet, egy folyamat: ami az autón van, azt egy gombbal átadják a
 * raktárnak (mindig mindent — nincs tételes válogatás), a raktárból pedig
 * kijelöléssel adják ki az átvevőnek. Egy kiadásból **négy okmány** lesz.
 */

interface AutoRakomany {
  sofor: string;
  rendszam: string | null;
  tetelek: number;
  hso_kg: number;
  mo_kg: number;
  /** HSO és moslék együtt — ennyi árut adtunk ki valójában. */
  osszes_kg: number;
  hordo: number;
  sorok: SzallitolevelSor[];
}

interface TechnikaiPartner {
  id: number;
  nev: string;
  cim: string | null;
  tipus: string | null;
}

/** A raktári lista állapotszűrője: előkészítve van-e szállításra. */
type AllapotSzuro = 'mind' | 'kesz' | 'nyers';

interface RaktarValasz {
  sorok: SzallitolevelSor[];
  atvevok: TechnikaiPartner[];
  fuvarozok: TechnikaiPartner[];
  /** Akik kiadóként választhatók — az ő aláírásuk kerül az okmányra. */
  kiadok: Array<{ id: number; nev: string; van_alairas: boolean }>;
}

interface KiadasSor {
  id: number;
  szam: string;
  kelt: string;
  atvevo_id: number | null;
  atvevo_nev: string | null;
  fuvarozo_nev: string | null;
  /** Ki szállít: a fuvarozó, vagy mi magunk a saját autónkkal. */
  szallito_nev: string;
  sofor: string | null;
  rendszam: string | null;
  tetelek: number;
  hso_kg: number;
  mo_kg: number;
  /** HSO és moslék együtt — ennyi árut adtunk ki valójában. */
  osszes_kg: number;
  hordo: number;
  sorok: SzallitolevelSor[];
}

const kg = (n: number) => (n ? `${szam(n, 0)} kg` : '—');

/** Egy okmány súlya: a hiteles mérés, ha megvan, különben a sofőré. */
const sulya = (s: SzallitolevelSor) => s.hiteles_kg ?? s.tomeg_kg ?? 0;

/* ------------------------------------------------------------------ */

export function Auton({ onHiba, onUzenet }: { onHiba: (h: string) => void; onUzenet: (u: string) => void }) {
  const en = useFelhasznalo();
  const kiurithet = useMuvelet('auto_kiurit');
  const [autok, setAutok] = useState<AutoRakomany[]>([]);
  const [kerdes, setKerdes] = useState<string | null>(null);

  const betolt = useCallback(async () => {
    try {
      const v = await api.get<{ autok: AutoRakomany[] }>('/api/szallitolevel/auton');
      setAutok(v.autok);
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'Az autók tartalmát nem sikerült betölteni.');
    }
  }, [onHiba]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  async function kiurit(sofor: string) {
    try {
      const v = await api.post<{ autok: AutoRakomany[] }>('/api/szallitolevel/auto-kiurites', { sofor });
      setAutok(v.autok);
      setKerdes(null);
      onUzenet(`${sofor} autója átadva a raktárnak.`);
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'Az átadás nem sikerült.');
    }
  }

  return (
    <>
      <div className="hint" style={{ marginBottom: 8 }}>
        {en?.admin || (en?.jogok.szlev ?? 0) >= 3
          ? 'Minden autó tartalma. Az átadásnál mindig az egész autó megy a raktárba.'
          : 'A saját autód tartalma. Az átadásnál mindig az egész autó megy a raktárba.'}
      </div>

      <div className="autok">
        {autok.map((a) => (
          <div className="autodoboz" key={a.sofor}>
            <div className="autofej">
              <b>{a.sofor}</b>
              <span>{a.rendszam ?? '—'}</span>
            </div>
            <div className="autoadat">
              <div>
                <em>Tétel</em>
                <strong>{a.tetelek}</strong>
              </div>
              <div>
                <em>HSO</em>
                <strong>{kg(a.hso_kg)}</strong>
              </div>
              <div>
                <em>Moslék</em>
                <strong>{kg(a.mo_kg)}</strong>
              </div>
              {/* HSO és moslék együtt — ennyi áru van az autón. (2026-10-13) */}
              <div>
                <em>Mindösszesen</em>
                <strong>{kg(a.osszes_kg)}</strong>
              </div>
              <div>
                <em>Hordó</em>
                <strong>{a.hordo} db</strong>
              </div>
            </div>
            <div className="autotetelek">
              {a.sorok.map((s) => (
                <span key={s.id} title={`${s.etterem_nev ?? ''} · ${s.kelt}`}>
                  {s.szam}
                </span>
              ))}
            </div>
            {kiurithet &&
              (kerdes === a.sofor ? (
                <div className="btns">
                  <button className="btn gold sm" onClick={() => void kiurit(a.sofor)}>
                    Igen, átadom a raktárnak
                  </button>
                  <button className="btn sm" onClick={() => setKerdes(null)}>
                    Mégse
                  </button>
                </div>
              ) : (
                <button className="btn sm pri" onClick={() => setKerdes(a.sofor)}>
                  Autó átadása a raktárnak
                </button>
              ))}
          </div>
        ))}
        {autok.length === 0 && <div className="hint">Most egyik autón sincs semmi.</div>}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

export function Raktar({
  onHiba,
  onUzenet,
  onKiadva,
  /** A címkenyomtató saját beállítása — a Szállítólevél képernyő tartja. */
  nyomtato = NYOMTATO_ALAP,
}: {
  onHiba: (h: string) => void;
  onUzenet: (u: string) => void;
  onKiadva: (id: number) => void;
  nyomtato?: NyomtatoBeallitas;
}) {
  const kiadhat = useMuvelet('kiadas');
  /* Aki kiadhat, az előkészítést is jelölhet — külön is adható jog. */
  const elokeszithet = useMuvelet('elokeszites') || kiadhat;
  /** Telefonon a kilenc oszlopos tábla helyett kártyák. (3.0 / 8.) */
  const mobil = useMobil();
  const [adat, setAdat] = useState<RaktarValasz | null>(null);
  const [kijelolt, setKijelolt] = useState<number[]>([]);
  const [atvevo, setAtvevo] = useState('');
  const [fuvarozo, setFuvarozo] = useState('');
  const [rendszam, setRendszam] = useState('');
  /** Ki adja ki — az ő mentett aláírása kerül az okmány Átadó rovatába. */
  const [kiado, setKiado] = useState('');
  /** A szállító aláírása a helyszínen — ugyanúgy, mint a begyűjtésnél. */
  const [szallitoAlairas, setSzallitoAlairas] = useState<string | null>(null);
  const [szallitoAlairo, setSzallitoAlairo] = useState('');
  /** Aláírás nélkül is ki lehet adni, de csak tudatosan: második kattintásra. */
  const [alairasNelkul, setAlairasNelkul] = useState(false);
  const [mentes, setMentes] = useState(false);
  const en = useFelhasznalo();

  const betolt = useCallback(async () => {
    try {
      const v = await api.get<RaktarValasz>('/api/szallitolevel/raktar');
      setAdat(v);
      /* Alapból az, aki épp be van lépve — őt kell a legritkábban átállítani. */
      setKiado((e) => e || String(v.kiadok?.find((k) => k.nev === en?.nev)?.id ?? ''));
      setKijelolt((k) => k.filter((id) => v.sorok.some((s) => s.id === id)));
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'A raktárat nem sikerült betölteni.');
    }
  }, [onHiba, en?.nev]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  /*
   * Típusszűrő a raktári listához. (2026-10-06, a megrendelő kérése)
   *
   * A kiadás jellemzően egy fajtára megy: külön a fáradt olaj, külön a
   * moslék. Így elég átváltani, „mindet kijelöl", és mehet.
   *
   * A kijelölést a szűrő NEM bántja: ami más fajtából már ki van jelölve,
   * az kijelölve marad, és az összesítő sávban látszik is. Ezért számol a
   * `kijeloltSorok` a teljes listából, nem a szűrtből.
   */
  const mindenSor = adat?.sorok ?? [];
  const [tipusSzuro, setTipusSzuro] = useState<'mind' | 'HSO' | 'MO'>('mind');
  /** Szállításra előkészítve-e: a raktáros munkájának állapota. (2026-10-16) */
  const [allapotSzuro, setAllapotSzuro] = useState<AllapotSzuro>('mind');
  /** Raklapcímke: egy raklapra egyféle áru megy, ezért elég a fajta. */
  const [raklapTipus, setRaklapTipus] = useState<'HSO' | 'MO'>('HSO');
  const [raklapDb, setRaklapDb] = useState(1);
  /*
   * Sofőr és nap szerinti szűrés a raktárban is. (2026-10-07, a megrendelő
   * kérése) A kiadás jellemzően egy sofőr egy napjára megy; típusra már
   * eddig is lehetett szűrni, de a kettő együtt teszi gyorssá a keresést.
   */
  const [soforSzuro, setSoforSzuro] = useState('');
  const [datumSzuro, setDatumSzuro] = useState('');
  const soforok = [
    ...new Set(mindenSor.map((s) => s.sofor).filter((s): s is string => !!s)),
  ].sort((a, b) => a.localeCompare(b, 'hu'));
  const alapSorok = mindenSor.filter(
    (s) => (!soforSzuro || s.sofor === soforSzuro) && (!datumSzuro || s.kelt === datumSzuro),
  );
  const tipusSorok =
    tipusSzuro === 'mind' ? alapSorok : alapSorok.filter((s) => s.tipus === tipusSzuro);
  /* Szállításra előkészítve: a raktáros már fóliázta, raklapra tette. */
  const sorok =
    allapotSzuro === 'mind'
      ? tipusSorok
      : tipusSorok.filter((s) => !!s.elokeszitve === (allapotSzuro === 'kesz'));

  const kijeloltSorok = mindenSor.filter((s) => kijelolt.includes(s.id));
  /** Ki van jelölve, de a szűrő most elrejti — erről szólunk. */
  const rejtettKijelolt = kijeloltSorok.length - sorok.filter((s) => kijelolt.includes(s.id)).length;

  const TIPUSOK: Array<['mind' | 'HSO' | 'MO', string]> = [
    ['mind', 'Mind'],
    ['HSO', 'Fáradt olaj (HSO)'],
    ['MO', 'Moslék'],
  ];
  /* A csipeken a sofőr/nap szűrésen belüli darabszám áll, hogy stimmeljen. */
  const tipusDb = (t: 'mind' | 'HSO' | 'MO') =>
    t === 'mind' ? alapSorok.length : alapSorok.filter((s) => s.tipus === t).length;

  /** Szállításra előkészítve — fóliázva, raklapon. (2026-10-16) */
  const ALLAPOTOK: Array<[AllapotSzuro, string]> = [
    ['mind', 'Mind'],
    ['kesz', 'Előkészítve'],
    ['nyers', 'Előkészítésre vár'],
  ];
  const allapotDb = (a: AllapotSzuro) =>
    a === 'mind' ? tipusSorok.length : tipusSorok.filter((s) => !!s.elokeszitve === (a === 'kesz')).length;
  const hso = kijeloltSorok.filter((s) => s.tipus === 'HSO');
  const mo = kijeloltSorok.filter((s) => s.tipus === 'MO');
  const ossz = (lista: SzallitolevelSor[], f: (s: SzallitolevelSor) => number) =>
    Math.round(lista.reduce((a, s) => a + f(s), 0) * 1000) / 1000;
  const okmanyok =
    (hso.length ? 1 : 0) + (mo.length ? 1 : 0) + (kijeloltSorok.length ? 1 : 0) + (hso.length ? 1 : 0);

  // A rendszám alapból a sofőré, de átírható, ha idegen kamion jön.
  useEffect(() => {
    if (!rendszam && kijeloltSorok[0]?.rendszam) setRendszam(kijeloltSorok[0].rendszam);
  }, [kijeloltSorok, rendszam]);

  /**
   * A kijelölt tételek megjelölése előkészítettként (vagy vissza).
   * A készlethez nem nyúl: az áru a raktárban marad. (2026-10-16)
   */
  async function elokeszitesJeloles(ertek: boolean) {
    if (kijelolt.length === 0 || mentes) return;
    setMentes(true);
    try {
      await api.post<{ sorok: SzallitolevelSor[] }>('/api/szallitolevel/raktar/elokeszites', {
        idk: kijelolt,
        ertek,
      });
      await betolt();
      setKijelolt([]);
      onUzenet(
        ertek
          ? `${kijelolt.length} tétel szállításra előkészítve.`
          : `${kijelolt.length} tételnél visszavontuk az előkészítést.`,
      );
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'A jelölés nem sikerült.');
    } finally {
      setMentes(false);
    }
  }

  /** Raklapcímke nyomtatása — a fóliázott raklap oldalára. (2026-10-16) */
  async function raklapot(elonezet: boolean) {
    if (elonezet) {
      raklapElonezet(raklapTipus, raklapDb, nyomtato.szeles);
      return;
    }
    try {
      const db = await raklapotNyomtat(raklapTipus, raklapDb, nyomtato);
      onUzenet(`${db} raklapcímke nyomtatva: ${raklapTipus}.`);
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'A címke nyomtatása nem sikerült.');
    }
  }

  async function kiad() {
    if (!atvevo || !kiado || kijelolt.length === 0 || mentes) return;
    /*
     * Aláírás nélkül is ki lehet adni — néha a fuvaros nem ér rá —, de
     * csak tudatosan: a második kattintás megy át. Felugró kérdés helyett,
     * ahogy a begyűjtésnél is.
     */
    if (!szallitoAlairas && !alairasNelkul) {
      setAlairasNelkul(true);
      onHiba('A szállító még nem írt alá. Kattints még egyszer a kiadáshoz, vagy írasd alá.');
      return;
    }
    setMentes(true);
    try {
      const k = await api.post<KiadasSor>('/api/szallitolevel/kiadas', {
        atvevo_id: Number(atvevo),
        fuvarozo_id: fuvarozo ? Number(fuvarozo) : null,
        kiado_id: Number(kiado),
        szallito_alairas: szallitoAlairas,
        szallito_alairo: szallitoAlairo.trim() || null,
        rendszam: rendszam || null,
        szallitolevel_idk: kijelolt,
      });
      setKijelolt([]);
      setSzallitoAlairas(null);
      setSzallitoAlairo('');
      setAlairasNelkul(false);
      await betolt();
      onUzenet(`Kiadás kész: ${k.szam} — ${k.tetelek} tétel. Az okmányok külön ablakban nyílnak.`);
      onKiadva(k.id);
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'A kiadás nem sikerült.');
    } finally {
      setMentes(false);
    }
  }

  return (
    <>
      <div className="kiadsav">
        <label>
          Átvevő telephely
          <select value={atvevo} onChange={(e) => setAtvevo(e.target.value)}>
            <option value="">— válassz —</option>
            {(adat?.atvevok ?? []).map((a) => (
              <option key={a.id} value={String(a.id)}>
                {a.nev}
                {a.tipus && a.tipus !== 'mindketto' ? ` (${a.tipus})` : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          Szállító (fuvarozó)
          <select value={fuvarozo} onChange={(e) => setFuvarozo(e.target.value)}>
            <option value="">— saját autó (a mi cégadatainkkal) —</option>
            {(adat?.fuvarozok ?? []).map((f) => (
              <option key={f.id} value={String(f.id)}>
                {f.nev}
              </option>
            ))}
          </select>
        </label>
        <label>
          Rendszám
          <input value={rendszam} onChange={(e) => setRendszam(e.target.value)} />
        </label>
        <label>
          Kiadta (a mi aláírásunk)
          <select value={kiado} onChange={(e) => setKiado(e.target.value)}>
            <option value="">— válassz —</option>
            {(adat?.kiadok ?? []).map((k) => (
              <option key={k.id} value={String(k.id)}>
                {k.nev}
                {k.van_alairas ? '' : ' (nincs mentett aláírás)'}
              </option>
            ))}
          </select>
        </label>
      </div>
      {kiado && !adat?.kiadok.find((k) => String(k.id) === kiado)?.van_alairas && (
        <div className="uzenet flag" style={{ marginTop: 6 }}>
          Ennek a kollégának nincs mentett aláírása, így az okmány Átadó rovatában csak a neve
          jelenik meg, az aláírás vonala üres marad. Az aláírás a saját nevére kattintva, a jobb
          felső sarokban rajzolható meg egyszer.
        </div>
      )}

      {/* A szállító a helyszínen ír alá — ugyanúgy, mint a begyűjtésnél. */}
      <div className="kiadsav" style={{ alignItems: 'flex-start' }}>
        <label>
          Aki a szállító részéről átveszi
          <input
            value={szallitoAlairo}
            placeholder={fuvarozo ? 'a fuvaros neve' : 'a sofőr neve'}
            onChange={(e) => setSzallitoAlairo(e.target.value)}
          />
        </label>
        <AlairasGomb
          cimke="Szállító aláírása"
          leiras="A fuvaros itt helyben írja alá; ez kerül az okmány Szállító rovatába."
          ertek={szallitoAlairas}
          onValtozott={(kep) => {
            setSzallitoAlairas(kep);
            if (kep) setAlairasNelkul(false);
          }}
        />
      </div>

      <div className="szlszuro">
        <label>
          Sofőr
          <select value={soforSzuro} onChange={(e) => setSoforSzuro(e.target.value)}>
            <option value="">mindegyik</option>
            {soforok.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label>
          Nap
          <input type="date" value={datumSzuro} onChange={(e) => setDatumSzuro(e.target.value)} />
        </label>
        {(soforSzuro || datumSzuro) && (
          <>
            <button
              className="btn sm"
              onClick={() => {
                setSoforSzuro('');
                setDatumSzuro('');
              }}
            >
              Szűrés törlése
            </button>
            <span className="hint">
              {ft(alapSorok.length)} tétel a raktárban {ft(mindenSor.length)} közül
            </span>
          </>
        )}
      </div>

      <div className="fchips" style={{ marginTop: 10 }}>
        {TIPUSOK.map(([k, cimke]) => (
          <button
            key={k}
            className={`fchip${tipusSzuro === k ? ' on' : ''}`}
            onClick={() => setTipusSzuro(k)}
          >
            {cimke}
            <b>{tipusDb(k)}</b>
          </button>
        ))}
        {rejtettKijelolt > 0 && (
          <span className="hint">
            További <b>{rejtettKijelolt}</b> kijelölt tétel másik fajtából — a kiadásba az is
            belemegy.
          </span>
        )}
      </div>

      {/* Szállításra előkészítve — a raktáros munkája. (2026-10-16) */}
      <div className="fchips" style={{ marginTop: 6 }}>
        {ALLAPOTOK.map(([k, cimke]) => (
          <button
            key={k}
            className={`fchip${allapotSzuro === k ? ' on' : ''}`}
            onClick={() => setAllapotSzuro(k)}
          >
            {cimke}
            <b>{allapotDb(k)}</b>
          </button>
        ))}
        {elokeszithet && (
          <>
            <button
              className="btn sm"
              disabled={kijelolt.length === 0 || mentes}
              title="A kijelölt tételek fóliázva, raklapon — a készlet nem változik tőle."
              onClick={() => void elokeszitesJeloles(true)}
            >
              Előkészítve jelölés
            </button>
            <button
              className="btn sm"
              disabled={kijelolt.length === 0 || mentes}
              onClick={() => void elokeszitesJeloles(false)}
            >
              Visszavonás
            </button>
          </>
        )}
      </div>

      {/*
        Raklapcímke. Egy raklapra egyféle áru kerül, ezért a címkén csak a
        fajta van — nagy betűvel, hogy messziről látszódjon. (2026-10-16)
      */}
      <div className="raklapsav" style={{ marginTop: 10 }}>
        <label>
          Raklapcímke
          <select
            value={raklapTipus}
            onChange={(e) => setRaklapTipus(e.target.value as 'HSO' | 'MO')}
          >
            <option value="HSO">HSO — fáradt olaj</option>
            <option value="MO">MO — moslék</option>
          </select>
        </label>
        <label>
          Darab
          <input
            type="number"
            min={1}
            max={50}
            value={raklapDb}
            onChange={(e) => setRaklapDb(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
          />
        </label>
        <button className="btn sm pri" onClick={() => void raklapot(false)}>
          Nyomtatás
        </button>
        <button className="btn sm" onClick={() => void raklapot(true)}>
          Előnézet
        </button>
        <span className="hint">
          A matricán csak a fajta van, nagy betűvel — a hordókon a sorszám úgyis rajta van.
        </span>
      </div>

      <div className="osszsav">
        <span>
          Kijelölt tétel <b>{kijelolt.length}</b>
        </span>
        <span>
          HSO <b>{kg(ossz(hso, (s) => s.hiteles_kg ?? s.tomeg_kg ?? 0))}</b>
        </span>
        <span>
          Moslék <b>{kg(ossz(mo, (s) => s.hiteles_kg ?? s.tomeg_kg ?? 0))}</b>
        </span>
        <span>
          Hordó <b>{ossz(kijeloltSorok, (s) => s.elvitt_hordo ?? 0)} db</b>
        </span>
        <span>
          Okmány <b>{okmanyok} db</b>
        </span>
        {kiadhat && (
          <button
            className="btn gold sm"
            disabled={!atvevo || !kiado || kijelolt.length === 0 || mentes}
            title={!kiado ? 'Add meg, ki adja ki — az ő aláírása kerül az okmányra.' : undefined}
            onClick={() => void kiad()}
          >
            {mentes
              ? 'Kiadás…'
              : alairasNelkul && !szallitoAlairas
                ? 'Kiadás aláírás nélkül'
                : 'Kiadás és okmányok'}
          </button>
        )}
      </div>

      {mobil ? (
        <div className="atlista">
          {sorok.map((s) => {
            const kijelolve = kijelolt.includes(s.id);
            return (
              <div
                className={`atkart${kijelolve ? ' jel' : ''}${s.elokeszitve ? ' elokesz' : ''}`}
                key={s.id}
              >
                <label className="atf">
                  <input
                    type="checkbox"
                    checked={kijelolve}
                    onChange={(e) =>
                      setKijelolt((k) => (e.target.checked ? [...k, s.id] : k.filter((x) => x !== s.id)))
                    }
                  />
                  <div>
                    <div className="atsz">
                      {s.szam}
                      {s.elokeszitve && <span className="elojel">előkészítve</span>}
                    </div>
                    <div className="atp">{s.etterem_nev}</div>
                    <div className="atc">
                      {s.kelt} · {s.sofor}
                    </div>
                  </div>
                </label>
                <div className="atadat">
                  <div>
                    <span>Típus</span>
                    <b>{s.tipus}</b>
                  </div>
                  <div>
                    <span>Tömeg</span>
                    <b>{kg(s.tomeg_kg ?? 0)}</b>
                  </div>
                  <div>
                    <span>Hiteles</span>
                    <b>{kg(s.hiteles_kg ?? 0)}</b>
                  </div>
                  <div>
                    <span>Hordó</span>
                    <b>{s.elvitt_hordo ?? '—'}</b>
                  </div>
                </div>
              </div>
            );
          })}
          {sorok.length === 0 && (
            <div className="blank">
              {tipusSzuro === 'mind' ? 'A raktár üres.' : 'Ebből a fajtából nincs a raktárban.'}
            </div>
          )}
        </div>
      ) : (
      <div className="matrix" style={{ maxHeight: 520, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th style={{ width: 34 }}>
                {/*
                  * „Mindet" a LÁTHATÓ sorokra ért: szűrve csak azokat jelöli
                  * ki, a másik fajtából már kijelölteket békén hagyja.
                  * (2026-10-06)
                  */}
                <input
                  type="checkbox"
                  title={
                    tipusSzuro === 'mind'
                      ? 'Minden raktári tétel kijelölése'
                      : 'A most látható tételek kijelölése'
                  }
                  checked={sorok.length > 0 && sorok.every((s) => kijelolt.includes(s.id))}
                  onChange={(e) =>
                    setKijelolt((k) => {
                      const lathato = sorok.map((s) => s.id);
                      return e.target.checked
                        ? [...new Set([...k, ...lathato])]
                        : k.filter((id) => !lathato.includes(id));
                    })
                  }
                />
              </th>
              <th>Sorszám</th>
              <th>Típus</th>
              <th>Partner</th>
              <th className="n">Kelt</th>
              <th className="n">Tömeg</th>
              <th className="n">Hiteles</th>
              <th className="n">Hordó</th>
              <th>Sofőr</th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((s) => (
              <tr
                key={s.id}
                className={
                  [kijelolt.includes(s.id) ? 'kij' : '', s.elokeszitve ? 'elokesz' : '']
                    .filter(Boolean)
                    .join(' ') || undefined
                }
              >
                <td className="c">
                  <input
                    type="checkbox"
                    checked={kijelolt.includes(s.id)}
                    onChange={(e) =>
                      setKijelolt((k) => (e.target.checked ? [...k, s.id] : k.filter((x) => x !== s.id)))
                    }
                  />
                </td>
                <td className="calc2" style={{ whiteSpace: 'nowrap' }}>
                  {s.szam}
                  {s.elokeszitve && (
                    <span
                      className="elojel"
                      title={`Szállításra előkészítve${
                        s.elokeszitve_ki ? ` — ${s.elokeszitve_ki}` : ''
                      }`}
                    >
                      előkészítve
                    </span>
                  )}
                </td>
                <td>{s.tipus}</td>
                <td className="pn">{s.etterem_nev}</td>
                <td className="calc2">{s.kelt}</td>
                <td className="calc2">{kg(s.tomeg_kg ?? 0)}</td>
                <td className="calc2">{kg(s.hiteles_kg ?? 0)}</td>
                <td className="calc2">{s.elvitt_hordo ?? ''}</td>
                <td>{s.sofor}</td>
              </tr>
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  {tipusSzuro === 'mind' ? 'A raktár üres.' : 'Ebből a fajtából nincs a raktárban.'}
                </td>
              </tr>
            )}
          </tbody>
          {/*
            * Összegsor a **látszó** sorokra, fajtánként külön. A felső sáv a
            * kijelölést összegzi; itt az kell, mennyi van összesen a
            * raktárban — a moslék súlya is. (2026-10-13, a megrendelő
            * jelzése)
            */}
          {sorok.length > 0 && (
            <tfoot>
              <tr>
                <td className="dcol" colSpan={4}>
                  Összesen {ft(sorok.length)} okmány
                </td>
                <td className="dcol" colSpan={2}>
                  HSO {kg(ossz(sorok.filter((s) => s.tipus === 'HSO'), sulya))}
                  {' · '}
                  moslék {kg(ossz(sorok.filter((s) => s.tipus === 'MO'), sulya))}
                </td>
                <td>{ossz(sorok, (s) => s.elvitt_hordo ?? 0)} db</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

export function Kiadottak({
  onHiba,
  onOkmanyok,
  onKuldes,
}: {
  onHiba: (h: string) => void;
  onOkmanyok: (id: number) => void;
  /** Csak akkor van gomb, ha a felhasználó küldhet levelet. */
  onKuldes?: (k: { id: number; szam: string; kelt: string; atvevo_id: number | null; atvevo_nev: string | null }) => void;
}) {
  const [sorok, setSorok] = useState<KiadasSor[]>([]);
  const [nyitott, setNyitott] = useState<number | null>(null);

  useEffect(() => {
    api
      .get<{ sorok: KiadasSor[] }>('/api/szallitolevel/kiadas')
      .then((v) => setSorok(v.sorok))
      .catch((h: Error) => onHiba(h.message));
  }, [onHiba]);

  return (
    <div className="matrix" style={{ maxHeight: 560, overflow: 'auto' }}>
      <table className="tt2">
        <thead>
          <tr>
            <th>Sorszám</th>
            <th className="n">Kelt</th>
            <th>Átvevő</th>
            <th>Szállító</th>
            <th className="n">Rendszám</th>
            <th className="n">Tétel</th>
            <th className="n">HSO</th>
            <th className="n">Moslék</th>
            <th className="n">Mindösszesen</th>
            <th className="n">Hordó</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {sorok.map((k) => (
            <>
              <tr key={k.id} className="kclick" onClick={() => setNyitott(nyitott === k.id ? null : k.id)}>
                <td className="calc2" style={{ whiteSpace: 'nowrap' }}>
                  {k.szam}
                </td>
                <td className="calc2">{k.kelt}</td>
                <td className="pn">{k.atvevo_nev}</td>
                <td>{k.szallito_nev}</td>
                <td className="calc2">{k.rendszam}</td>
                <td className="calc2">{k.tetelek}</td>
                <td className="calc2">{kg(k.hso_kg)}</td>
                <td className="calc2">{kg(k.mo_kg)}</td>
                {/*
                  * HSO és moslék együtt: ennyi árut adtunk ki valójában.
                  * (2026-10-13, a megrendelő kérése)
                  */}
                <td className="calc2">
                  <b>{kg(k.osszes_kg)}</b>
                </td>
                <td className="calc2">{k.hordo} db</td>
                <td className="act">
                  <button
                    className="btn sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOkmanyok(k.id);
                    }}
                  >
                    Okmányok
                  </button>
                  {onKuldes && (
                    <button
                      className="btn sm"
                      title="Az okmányok elküldése az átvevőnek"
                      onClick={(e) => {
                        e.stopPropagation();
                        onKuldes(k);
                      }}
                    >
                      Küldés
                    </button>
                  )}
                </td>
              </tr>
              {nyitott === k.id &&
                k.sorok.map((s) => (
                  <tr key={`${k.id}-${s.id}`} className="ktet">
                    <td />
                    <td className="calc2" style={{ whiteSpace: 'nowrap' }}>
                      {s.szam}
                    </td>
                    <td>{s.tipus}</td>
                    <td className="pn">{s.etterem_nev}</td>
                    <td className="calc2">{s.kelt}</td>
                    <td className="calc2">{kg(s.tomeg_kg ?? 0)}</td>
                    <td className="calc2">{kg(s.hiteles_kg ?? 0)}</td>
                    <td className="calc2">{s.szurt_kg ? `szűrt ${szam(s.szurt_kg, 0)} kg` : ''}</td>
                    <td className="calc2">{s.elvitt_hordo ?? ''}</td>
                    <td />
                  </tr>
                ))}
            </>
          ))}
          {sorok.length === 0 && (
            <tr>
              <td colSpan={11} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                Még nem volt kiadás.
              </td>
            </tr>
          )}
        </tbody>
        {/*
          * Összegsor: a listázott kiadások együtt. Enélkül nem látszott,
          * összesen mennyi árut adtunk ki. (2026-10-13, a megrendelő kérése)
          */}
        {sorok.length > 0 && (
          <tfoot>
            <tr>
              <td className="dcol" colSpan={5}>
                Összesen {ft(sorok.length)} kiadás
              </td>
              <td>{ft(sorok.reduce((a, k) => a + k.tetelek, 0))}</td>
              <td>{kg(sorok.reduce((a, k) => a + k.hso_kg, 0))}</td>
              <td>{kg(sorok.reduce((a, k) => a + k.mo_kg, 0))}</td>
              <td>
                <b>{kg(sorok.reduce((a, k) => a + k.osszes_kg, 0))}</b>
              </td>
              <td>{ft(sorok.reduce((a, k) => a + k.hordo, 0))} db</td>
              <td />
            </tr>
          </tfoot>
        )}
      </table>
      <div className="hint" style={{ padding: '6px 4px' }}>
        A sorra kattintva látszanak a hozzá tartozó begyűjtő szállítólevelek. A „Mindösszesen" a
        kiadott áru teljes súlya: HSO és moslék együtt.
      </div>
    </div>
  );
}
